"use client";

import { useReportWebVitals } from "next/web-vitals";
import { useCallback } from "react";

/**
 * Web Vitals Monitoring Component
 *
 * Tracks Core Web Vitals metrics in real-time:
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - FID (First Input Delay)
 * - INP (Interaction to Next Paint)
 * - LCP (Largest Contentful Paint)
 * - TTFB (Time to First Byte)
 *
 * In development: only logs metrics that need improvement
 * In production: sends to analytics endpoint and Google Analytics 4
 */
export function WebVitals() {
  const sendToAnalytics = useCallback(
    (metric: {
      id: string;
      name: string;
      value: number;
      rating: "good" | "needs-improvement" | "poor";
      delta: number;
      navigationType: string;
      attribution?: unknown;
    }) => {
      // Development: log all metrics to track improvements
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log(`[Web Vitals] ${metric.name}:`, {
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
          id: metric.id,
          navigationType: metric.navigationType,
        });
        return;
      }

      // Production analytics tracking
      if (typeof window === "undefined") return;

      // Google Analytics 4 tracking
      if ("gtag" in window && typeof window.gtag === "function") {
        window.gtag("event", metric.name, {
          value: Math.round(metric.value),
          event_category: "Web Vitals",
          event_label: metric.id,
          non_interaction: true,
          custom_parameter_1: metric.rating,
          custom_parameter_2: metric.navigationType,
        });
      }

      // Send to custom analytics endpoint using sendBeacon for reliability
      const analyticsData = {
        metric: metric.name,
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
        navigationType: metric.navigationType,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      if ("sendBeacon" in navigator) {
        const blob = new Blob([JSON.stringify(analyticsData)], {
          type: "application/json",
        });
        navigator.sendBeacon("/api/web-vitals", blob);
      }

      // Log poor performance metrics for debugging
      if (metric.rating === "poor") {
        // eslint-disable-next-line no-console
        console.warn(
          `[Web Vitals Warning] Poor ${metric.name}: ${metric.value}ms`,
        );
      }
    },
    [],
  );

  useReportWebVitals(sendToAnalytics);

  return null;
}
